import { dataService } from '@app/modules/data';
import { DataObject, ObjectOptions } from './base';

import { PlaceService } from './svcplace';

/** Drivers de datafono soportados (valor de `PAYTERMINAL.model`). */
export const PAYTERMINAL_PAYTEF   = 'PAYTEF';
export const PAYTERMINAL_PROSEGUR = 'PROSEGUR';
export const PAYTERMINAL_CASHLOGY = 'CASHLOGY';

export interface _PaymentTerminalDevice {
    rasppi: number;
    status: string;
    name: string;
    model: string;         // driver del datafono: PAYTEF | PROSEGUR | CASHLOGY
    host: string;          // como se direcciona: IP (PAYTEF) o alias MDW (PROSEGUR: PaymentTerminal, PaymentTerminal2...)
    onpaycash: boolean;    // auto-print the ticket receipt on this terminal when payment is cash
    onpaycard: boolean;    // auto-print the ticket receipt on this terminal when payment is card (paytef)
    nocash: boolean;       // when true, the slim-device app on this terminal blocks cash payment
};

interface _PaymentTerminalDeviceData extends _PaymentTerminalDevice {
    objid?: number;
    _uuid?: string;
    created?: Date;
    updated?: Date;
};

abstract class PaymentTerminalDeviceData extends DataObject {
    protected _terminal: _PaymentTerminalDeviceData = {
        rasppi: null,
        status: null,
        name: null,
        model: null,
        host: null,
        onpaycash: false,
        onpaycard: false,
        nocash: false
    };

    constructor(table: string, objid: string, data: dataService, objoptions: ObjectOptions){
        super(table, objid, data, objoptions);
        this._terminal.created = new Date();
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    get created(){
        return this._terminal.created;
    }

    get updated(): Date {
        return this._terminal.updated || this._terminal.created;
    }

    get rasppi() : PlaceService {
        return this._children['rasppi'] || null;
    }

    set rasppi(value: PlaceService){
        if (this.SetChild('rasppi', value, 'rasppi')){
            this.ToUpdate = true;
        }
    }

    get status(): string{
        return this._terminal.status;
    }

    set status(value: string){
        if (this.patchValue(this._terminal, 'status', value)){
            this.ToUpdate = true;
        }
    }

    get name(): string{
        return this._terminal.name;
    }

    set name(value: string){
        if (this.patchValue(this._terminal, 'name', value)){
            this.ToUpdate = true;
        }
    }

    get model(): string{
        return this._terminal.model;
    }

    set model(value: string){
        if (this.patchValue(this._terminal, 'model', value)){
            this.ToUpdate = true;
        }
    }

    get host(): string{
        return this._terminal.host;
    }

    set host(value: string){
        if (this.patchValue(this._terminal, 'host', value)){
            this.ToUpdate = true;
        }
    }

    get onpaycash(): boolean{
        return !!this._terminal.onpaycash;
    }

    set onpaycash(value: boolean){
        if (this.patchValue(this._terminal, 'onpaycash', !!value)){
            this.ToUpdate = true;
        }
    }

    get onpaycard(): boolean{
        return !!this._terminal.onpaycard;
    }

    set onpaycard(value: boolean){
        if (this.patchValue(this._terminal, 'onpaycard', !!value)){
            this.ToUpdate = true;
        }
    }

    get nocash(): boolean{
        return !!this._terminal.nocash;
    }

    set nocash(value: boolean){
        if (this.patchValue(this._terminal, 'nocash', !!value)){
            this.ToUpdate = true;
        }
    }

    /****************************/
    /* COMMIT OPERATION         */
    /****************************/

    protected get Change() {
        return {
            rasppi: this._terminal.rasppi,
            status: this._terminal.status,
            name: this._terminal.name,
            model: this._terminal.model,
            host: this._terminal.host,
            onpaycash: this._terminal.onpaycash ? 1 : 0,
            onpaycard: this._terminal.onpaycard ? 1 : 0,
            nocash: this._terminal.nocash ? 1 : 0
        };
    }

    protected get Depend() {
        return {
            rasppi: { item: this.rasppi, relation_info: { to: 'payment_terminals', by: 'rasppi' } }   // this[by -> 'rasppi'][to -> 'payment_terminals'] => this
        };
    }

    protected get Children(){
        return [ /* empty */];
    }

    /****************************/
    /* DATA OBJECT              */
    /****************************/

    private _patchData(_terminal: _PaymentTerminalDevice){
        let _toUpdate = false;

        _toUpdate = this.patchValue(this._terminal, 'rasppi', _terminal['rasppi']) || _toUpdate;
        _toUpdate = this.patchValue(this._terminal, 'status', _terminal['status']) || _toUpdate;
        _toUpdate = this.patchValue(this._terminal, 'name', _terminal['name']) || _toUpdate;
        _toUpdate = this.patchValue(this._terminal, 'model', _terminal['model']) || _toUpdate;
        _toUpdate = this.patchValue(this._terminal, 'host', _terminal['host']) || _toUpdate;
        _toUpdate = this.patchValue(this._terminal, 'onpaycash', !!_terminal['onpaycash']) || _toUpdate;
        _toUpdate = this.patchValue(this._terminal, 'onpaycard', !!_terminal['onpaycard']) || _toUpdate;
        _toUpdate = this.patchValue(this._terminal, 'nocash', !!_terminal['nocash']) || _toUpdate;

        return _toUpdate;
    }

    set Data(_terminal: _PaymentTerminalDevice){
        this.patchValue(this._terminal, 'created', _terminal['created']);
        this.patchValue(this._terminal, 'updated', _terminal['updated']);

        if (this._patchData(_terminal)){
            this.ToUpdate = true;
        }
    }

    get Info(){
        return this._terminal;
    }

    set Info(value){
        this.DoPatchValues(value);
    }

    private DoPatchValues(_terminal: _PaymentTerminalDevice){
        this._patchData(_terminal);

        if (_terminal['rasppi']){     // update children: 'rasppi'
            let _objid = _terminal['rasppi'].toString();
            this.SetChild('rasppi', new PlaceService(_objid, this.data, this._objoptions), 'rasppi')
        }
        else {
            this.SetChild('rasppi', null, 'rasppi');
        }
    }

    private _ddbb(info): _PaymentTerminalDeviceData {
        let _terminal: _PaymentTerminalDeviceData = {
            objid: info['objid'] ? parseInt(info['objid']) : null,
            created: new Date(Date.parse(this.mysqlToDateStr(info['created']))),
            updated: new Date(Date.parse(this.mysqlToDateStr(info['updated']))),
            rasppi: info['rasppi'] ? parseInt(info['rasppi']) : null,
            status: info['status'],
            name: info['name'],
            model: info['model'] || PAYTERMINAL_PAYTEF,
            host: info['host'],
            onpaycash: !!parseInt(info['onpaycash'] || 0),
            onpaycard: !!parseInt(info['onpaycard'] || 0),
            nocash: !!parseInt(info['nocash'] || 0)
        };
        return _terminal;
    }

    protected _OnUpdate(info){
        let _terminal = this._ddbb(info);
        this.patchValue(this._terminal, 'objid', _terminal['objid']);
        this.patchValue(this._terminal, 'created', _terminal['created']);
        this.patchValue(this._terminal, 'updated', _terminal['updated']);
        this.DoPatchValues(_terminal);
    }
}

export class PaymentTerminal extends PaymentTerminalDeviceData {
    constructor(objid: string, data: dataService, objoptions: ObjectOptions = null){
        super('PAYTERMINAL', objid, data, objoptions);
    }

    Copy(store: Array<DataObject> = []): PaymentTerminal {
        return this._Copy(store) as PaymentTerminal;
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    get status(): string {
        return super.status;
    }

    set status(value: string){
        if (this.status == 'DE'){
            return;     // cannot modify deleted items
        }

        super.status = value;

        if (this.rasppi){
            if ((this.status == 'DE') && (this.ToInsert) && (!this.CopyOf || this.CopyOf.ToInsert)){
                this.rasppi.DelPaymentTerminal(this);
            }
            else {
                this.rasppi.DoRefresh('PAYTERMINAL');
            }
        }
    }

    /****************************/
    /* CUSTOM METHODS           */
    /****************************/

    get IsValid(){
        return (this.status && this.status != 'DE');
    }

    get IsDeleted(){
        return (this.status == 'DE');
    }

    /**
     * ¿Es un datafono PAYTEF? Solo PAYTEF es a la vez datafono de cobro por IP
     * e IMPRESORA de recibos (comando PRINT). PROSEGUR/CASHLOGY cobran por el
     * MDW/ConnectorPlus del cajon y su `host` es un alias, no una IP Paytef:
     * NO deben entrar en los caminos de impresion Paytef. Filas antiguas sin
     * `model` se tratan como PAYTEF (era lo unico que existia). Mismo criterio
     * que `isPaytefTerminal` de ui-paytef-utils.
     */
    get IsPaytef(){
        return !this.model || (this.model == PAYTERMINAL_PAYTEF);
    }
}
